import {Dossier, Partenaire, Utilisateur} from "../../components/models";
import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {getUserCredentials, login as loginRequest} from "../../components/requests";
import {jwtDecode} from "jwt-decode";
import LoadingPage from "../pages/components/LoadingPage";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";

export type TwoFactorStep = 'none' | 'verify' | 'setup';

interface JwtPayload {
  exp?: number;
  twoFactorComplete?: boolean;

  [key: string]: any;
}

interface AuthContextType {
  currentUser: Utilisateur | null;
  setTrigger: any;
  login: (email: string, password: string) => Promise<boolean | string | 'two_factor' | 'activate_account'>;
  logout: () => void;
  getUserRole: () => string;
  isAdmin: boolean;
  isGestionnaire: boolean;
  canEditDossier: (dossier: Dossier) => boolean | void;
  getUserDossiers: () => Dossier[] | void;
  getPartenaire: () => Partenaire[];
  twoFactorStep: TwoFactorStep;
  setTwoFactorStep: (twoFactorStep: TwoFactorStep) => void;
  completeTwoFactor: (token: string) => Promise<void>;
  isLoading: boolean;
  canView: (rubriqueCode: string, user?: any) => boolean;
  canWrite: (rubriqueCode: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({children}) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<Utilisateur | null>(null);
  const [trigger, setTrigger] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [twoFactorStep, setTwoFactorStep] = useState<TwoFactorStep>('none');
  const [autorisations, setAutorisations] = useState<any[]>([]);
  const hasLoadedUser = useRef(false);

  const MERCURE_URL = `${process.env.REACT_APP_MERCURE_URL ?? ""}/.well-known/mercure`;

  // Pour la redirection après login
  // !vue Accueil => vue Dossier ...
  const routesPriority = useMemo(() => [
    {key: "Accueil", path: "/accueil"},
    {key: "Dossier", path: "/dossiers"},
    {key: "Tableau de bord", path: "/dashboard"},
    {key: "Parametrage", path: "/parametrage/utilisateurs"},
  ], []);

  // Méthode de déconnexion et redirection vers la page de connexion
  const logout = useCallback(() => {
    localStorage.removeItem('userEmail');
    localStorage.removeItem('accessToken');
    setCurrentUser(null);
    setTwoFactorStep('none');
    navigate("/auth/login");
  }, [navigate]);

  // Pour la lecture
  const canView = useCallback((rubriqueCode: string, user = currentUser): boolean => {
    if (!user?.permissions) return false;
    const perm = user.permissions[rubriqueCode as keyof Permissions];
    return !!(perm?.view || perm?.write);
  }, [currentUser]);

  // Pour l'écriture
  const canWrite = useCallback((rubriqueCode: string): boolean => {
    if (!currentUser?.permissions) return false;
    const perm = currentUser.permissions[rubriqueCode as keyof Permissions];
    return !!perm?.write;
  }, [currentUser]);

  // Méthode pour la vérification des routes ayant les permissions requises après login
  const getFirstAccessiblePath = useCallback((user): string | null => {
    for (const route of routesPriority) {
      if (canView(route.key, user)) {
        return route.path;
      }
    }
    return "/documentation";
  }, [canView, routesPriority]);

  // Méthode de connexion
  const login = useCallback(async (email: string, password: string): Promise<boolean | string | 'two_factor' | 'activate_account'> => {
    try {
      const response = await loginRequest(email, password);
      if (response && response.status === 200) {
        const data = response.data;

        if (data?.invite) {
          return 'activate_account';
        }

        localStorage.setItem('userEmail', email);
        localStorage.setItem('accessToken', data.token);

        // Check if 2FA is required
        if (data.twoFactorRequired) {
          setTwoFactorStep('verify');
          navigate('/auth/verify-2fa', {
            state: {
              id: data?.id,
              twoFactorMethod: data?.twoFactorMethod
            }
          });
          return 'two_factor';
        }

        if (data.twoFactorSetupRequired) {
          setTwoFactorStep('setup');
          navigate('/auth/setup-2fa', {
            state: {
              id: data?.id,
              twoFactorMethod: data?.twoFactorMethod
            }
          });
          return 'two_factor';
        }

        // No 2FA needed (shouldn't happen with mandatory 2FA, but handle gracefully)
        const user = await getUserCredentials(email);
        if (user && user.status === 200 && user.data !== undefined) {
          setCurrentUser(user.data);
          // Rédirection après succès du login
          const target = getFirstAccessiblePath(user.data);
          navigate(target ?? "/404");
          return true;
        }
        return false;
      }
      return false;
    } catch (error) {
      logout();
      console.error(error);
      if (error?.status === 429) return error?.response?.data?.detail
      return false;
    }
  }, [logout, navigate, getFirstAccessiblePath]);

  // Complete 2FA: replace partial JWT with full JWT
  const completeTwoFactor = useCallback(async (token: string) => {
    localStorage.setItem('accessToken', token);
    setTwoFactorStep('none');

    const savedEmail = localStorage.getItem('userEmail');
    if (savedEmail) {
      try {
        const user = await getUserCredentials(savedEmail);
        if (user && user.status === 200 && user.data?.actif === true) {
          setCurrentUser(user.data);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, []);

  // Listen for 2fa-required events from the API interceptor
  useEffect(() => {
    const handle2FARequired = () => {
      logout();
    };
    window.addEventListener('2fa-required', handle2FARequired);
    return () => window.removeEventListener('2fa-required', handle2FARequired);
  }, [logout]);

  // Persister l'utilisateur connecté et verifier si le token n'est pas expiré
  useEffect(() => {
    // Si le trigger est activé, suppression du ref hasLoadedUser pour activer tout l'useEffect
    if (trigger) hasLoadedUser.current = false;

    const savedToken = localStorage.getItem("accessToken");
    const savedEmail = localStorage.getItem("userEmail");

    // Si il n'y a pas d'accessToken ou d'email dans le localStorage
    if (!savedToken || !savedEmail) {
      setIsLoading(false);
      return;
    }

    try {
      // Décodage du token dans le localStorage
      const decodedToken = jwtDecode<JwtPayload>(savedToken);
      if (!decodedToken) {
        logout();
        setIsLoading(false);
        return;
      }

      // Vérification si la date d'expiration du token dépase la date et l'heure maintenant
      if (decodedToken.exp && Math.floor(Date.now() / 1000) > decodedToken.exp) {
        logout();
        setIsLoading(false);
        return;
      }

      // Check if this is a partial 2FA token
      if (decodedToken.twoFactorComplete === false) {
        // Token is partial - user still needs 2FA
        // Determine if setup or verify based on what we can infer
        // If we don't have a current twoFactorStep set, default to 'verify'
        if (twoFactorStep === 'none') {
          setTwoFactorStep('verify');
        }
        setIsLoading(false);
        return;
      }

      // Si le ref hasLoadedUser a été reactivé
      if (hasLoadedUser.current) {
        setIsLoading(false);
        return;
      }

      // Si il n'y a pas de currentUser ou que le trigger est activé
      if (!currentUser || trigger) {
        getUserCredentials(savedEmail)
          .then((res) => {
            if (res.status === 200 && res.data?.actif === true)
              setCurrentUser(res.data);
          })
          .catch(error => console.error(error))
          .finally(() => setIsLoading(false));
      }

      // Définition du ref hasLoadedUser sur true pour ne plus refaire tout l'useEffect et s'arrêter à la première ligne
      hasLoadedUser.current = true;
      // Si trigger est activé
      if (trigger) setTrigger(false);
    } catch (e) {
      logout();
      console.error(e);
    }
  }, [trigger, currentUser, logout, twoFactorStep]);

  // connection sur le topic role_rubriques de mercure
  useEffect(() => {
    // Connexion sur mercure
    const url = new URL(MERCURE_URL);
    url.searchParams.append("topic", "role_rubriques");
    const eventSource = new EventSource(url);

    // Sur chaque message
    eventSource.onmessage = (e) => {
      // Verification avant la version JSON
      if (!e.data) return;

      // Decoddage des données
      const parsedData = JSON.parse(e.data);
      const roleId = parsedData?.utilisateurRole?.id;
      const code = parsedData?.rubrique?.code ?? parsedData?.rubriqueCategorie?.code;

      // Enregistrement des rubriques sur un rôle
      setAutorisations(prev => {
        const permissions = {
          view: parsedData?.lecture,
          write: parsedData?.ecriture,
        };

        // Modifier si les données sont déjà existante
        const index = prev.findIndex(r => r[0] === roleId);
        if (index !== -1) {
          const updated = [...prev];
          updated[index] = [roleId, {...updated[index][1], [code]: permissions}];
          return updated;
        }
        // Sinon créer une nouvelle ligne
        return [...prev, [roleId, {[code]: permissions}]];
      });
    };
    return () => eventSource.close();
  }, [MERCURE_URL]);

  // mise à jour et controlle des permissions de l'utilisateur connecté
  useEffect(() => {
    // Verification de autorisations pour eviter une boucle infinie
    if (autorisations.length === 0) return;
    // Création d'un timeout pour bien charger les données de mercure
    const timeoutId = setTimeout(() => {
      autorisations.forEach(autorisation => {
        // Verification si l'utilisateur connecté a le role à jours venant de mercure
        if (autorisation?.[0] === currentUser?.utilisateurRole?.id) {
          // Modification des permissions de l'utilisateur
          const updatedUser = {
            ...currentUser,
            permissions: autorisation?.[1]
          };
          setCurrentUser(updatedUser);
          // // navigation vers le premier chemin accessible
          // navigate(getFirstAccessiblePath(updatedUser));
          // Notification
          toast.info('Les rubriques associées à votre rôle ont été actualisées');
        }
      })
      // Nettoyage des données pour conserver le flux de données de mercure à jour
      setAutorisations([]);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [autorisations, currentUser]);

  // Méthode pour obtenir le role de l'utilisateur connecté
  const getUserRole = useCallback((): string => {
    if (!currentUser) return '';

    return currentUser?.utilisateurRole?.libelle || '';
  }, [currentUser]);

  // Méthode pour savoir si l'utilisateur connecté est un administrateur
  const isAdmin = (currentUser?.utilisateurRole?.code === 'ADMIN');

  // Méthode pour savoir si l'utilisateur connecté est un gestionnaire
  // const isGestionnaire = (currentUser?.utilisateurRole?.code === 'GEST');
  const isGestionnaire = (currentUser?.utilisateurRole?.partenaire === true);

  // Méthode d'obtention des partenaires de l'utilisateur connecté
  const getPartenaire = useCallback((): Partenaire[] => {
    if (!currentUser) return [];
    if (!currentUser?.partenaires) return [];
    return currentUser.partenaires.map((gestionnaire) => {
      return {id: gestionnaire.partenaire.id, nom: gestionnaire.partenaire.nom};
    });
  }, [currentUser]);

  const canEditDossier = useCallback((dossier: Dossier): boolean | void => {
    // if (!currentUser) return false;
    // if (currentUser.role === UserRole.SUPER_ADMIN) return true;
    // // IMPORTANT: Les partenaires peuvent modifier leurs propres dossiers
    // return dossier.partenaire.nom === currentUser.partenaire;
  }, []);

  const getUserDossiers = useCallback((): Dossier[] | void => {
    // if (!currentUser) return [];
    // if (currentUser.role === UserRole.SUPER_ADMIN) return DOSSIERS;
    // return DOSSIERS.filter(d => d.partenaire.nom === currentUser.partenaire);
  }, []);

  // Affichage de la page de chargement si la page charge, sinon l'enfant
  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setTrigger,
        login,
        logout,
        getUserRole,
        isAdmin,
        isGestionnaire,
        canEditDossier,
        getUserDossiers,
        getPartenaire,
        twoFactorStep,
        setTwoFactorStep,
        completeTwoFactor,
        isLoading,
        canView,
        canWrite,
      }}>
      {isLoading ? <LoadingPage/> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
